import React, { useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUpRight,
  Check,
  CircleAlert,
  ClipboardList,
  Download,
  Ruler,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { StructuredData } from "@/components/StructuredData";
import {
  EXTRA_PRIVACY_ADDITION_PER_STALL_CENTS,
  INSTALLATION_ADDITION_PER_STALL_CENTS,
  PARTITION_BUDGET_LEAD_TIME_BOUNDARY,
  PARTITION_BUDGET_TYPICAL_LEAD_TIME,
  PARTITION_BUDGET_UPDATED_LABEL,
  formatPartitionBudgetCurrency,
  getPartitionBudgetAccessibilityLabel,
  getPartitionBudgetEstimate,
  getPartitionBudgetMaterial,
  getPartitionBudgetMaterialGuidePdfUrl,
  getPartitionBudgetMaterialPriceRange,
  partitionBudgetMaterialProductVisuals,
  partitionBudgetMaterials,
  partitionBudgetStallCounts,
  type PartitionBudgetAccessibility,
  type PartitionBudgetEstimate,
  type PartitionBudgetInput,
  type PartitionBudgetMaterialId,
  type PartitionBudgetStallCount,
} from "@/data/partitionBudgetEstimates";
import { siteConfig } from "@/data/site";
import { trackGa4CalculatorGuideDownload } from "@/lib/ga4";
import { buildPartitionBudgetQuoteHandoff, savePartitionBudgetQuoteHandoff } from "@/lib/partitionBudgetQuote";

const defaultInput: PartitionBudgetInput = {
  materialId: "powder-coated-steel",
  stalls: 1,
  accessibility: "standard",
  privacy: "standard",
  installation: "not-included",
};

const materialRanges = partitionBudgetMaterials.map((material) => ({
  material,
  materialRange: getPartitionBudgetMaterialPriceRange(material.id),
  visual: partitionBudgetMaterialProductVisuals[material.id],
}));

type BudgetMaterial = (typeof partitionBudgetMaterials)[number];

function MaterialOption({
  selected,
  materialId,
  label,
  copy,
  lowCents,
  highCents,
  onClick,
}: {
  selected: boolean;
  materialId: PartitionBudgetMaterialId;
  label: string;
  copy: string;
  lowCents: number;
  highCents: number;
  onClick: () => void;
}) {
  const visual = partitionBudgetMaterialProductVisuals[materialId];

  return (
    <button type="button" className="budget-material-tile" aria-pressed={selected} onClick={onClick}>
      <figure>
        <img src={visual.src} alt="" width="900" height="900" loading="lazy" />
        <figcaption>{visual.caption}</figcaption>
      </figure>
      <span className="budget-material-tile__copy">
        <span className="budget-material-tile__title-row">
          <strong>{label}</strong>
          {selected ? <em><Check aria-hidden="true" /> Selected</em> : null}
        </span>
        <small>{copy}</small>
        <b>{formatPartitionBudgetCurrency(lowCents)}–{formatPartitionBudgetCurrency(highCents)} <span>base / compartment</span></b>
      </span>
    </button>
  );
}

function CountOption({ selected, count, onClick }: { selected: boolean; count: PartitionBudgetStallCount; onClick: () => void }) {
  return (
    <button type="button" className="budget-count-option" aria-pressed={selected} onClick={onClick}>
      <strong>{count}</strong>
      <span>{count === 1 ? "compartment" : "compartments"}</span>
      {selected ? <Check aria-label="Selected" /> : null}
    </button>
  );
}

function AddOnToggle({ selected, title, addition, copy, onClick }: { selected: boolean; title: string; addition: string; copy: string; onClick: () => void }) {
  return (
    <button type="button" className="budget-addition-toggle" aria-pressed={selected} onClick={onClick}>
      <span className="budget-addition-toggle__state">{selected ? "Added to plan" : "Optional addition"}</span>
      <strong>{title}</strong>
      <b>{addition}</b>
      <small>{copy}</small>
      <span className="budget-addition-toggle__control" aria-hidden="true"><span /></span>
    </button>
  );
}

function AccessibilityOption({ selected, label, copy, onClick }: { selected: boolean; label: string; copy: string; onClick: () => void }) {
  return (
    <button type="button" className="budget-context-option" aria-pressed={selected} onClick={onClick}>
      <span><ShieldCheck aria-hidden="true" /></span>
      <strong>{label}</strong>
      <small>{copy}</small>
      {selected ? <Check aria-label="Selected" /> : null}
    </button>
  );
}

function BudgetResult({ input, material, estimate, onDownloadGuide, onSendToQuote }: { input: PartitionBudgetInput; material: BudgetMaterial; estimate: PartitionBudgetEstimate | undefined; onDownloadGuide: () => void; onSendToQuote: () => void }) {
  const materialVisual = partitionBudgetMaterialProductVisuals[input.materialId];
  const baseRange = getPartitionBudgetMaterialPriceRange(input.materialId);
  const selectedAdditions = [
    input.privacy === "extra-privacy" ? "Extra privacy" : null,
    input.installation === "included" ? "Installation" : null,
  ].filter((value): value is string => Boolean(value));

  return (
    <aside id="budget-result" className="budget-live-result" aria-live="polite" aria-labelledby="budget-result-title">
      <div className="budget-live-result__eyebrow"><Ruler aria-hidden="true" /><span>Your live planning range</span></div>
      <p className="budget-live-result__material">{material.name}</p>
      <h2 id="budget-result-title">{estimate ? `${formatPartitionBudgetCurrency(estimate.lowCents)}–${formatPartitionBudgetCurrency(estimate.highCents)}` : "Selection needs review"}</h2>
      <p className="budget-live-result__basis">{estimate?.basisLabel ?? "Planning range"} for <b>{input.stalls} {input.stalls === 1 ? "compartment" : "compartments"}</b></p>

      <div className="budget-live-result__plan" aria-label="Current project direction">
        <span>{input.stalls} {input.stalls === 1 ? "compartment" : "compartments"}</span>
        <span>{selectedAdditions.length > 0 ? selectedAdditions.join(" + ") : "No priced additions"}</span>
        <span>{input.accessibility === "accessibility" ? "Accessibility context" : "Standard context"}</span>
      </div>

      <dl>
        <div><dt>Base material / compartment</dt><dd>{formatPartitionBudgetCurrency(baseRange.lowCents)}–{formatPartitionBudgetCurrency(baseRange.highCents)}</dd></div>
        {estimate?.privacyAdditionPerStallCents ? <div><dt>Extra privacy</dt><dd>+{formatPartitionBudgetCurrency(estimate.privacyAdditionPerStallCents)} / compartment</dd></div> : null}
        {estimate?.installationAdditionPerStallCents ? <div><dt>Installation</dt><dd>+{formatPartitionBudgetCurrency(estimate.installationAdditionPerStallCents)} / compartment</dd></div> : null}
        <div><dt>Quote context</dt><dd>{getPartitionBudgetAccessibilityLabel(input.accessibility)} <span>(does not change the range)</span></dd></div>
      </dl>

      <figure className="budget-live-result__visual" key={input.materialId}>
        <img src={materialVisual.src} width="900" height="900" alt={materialVisual.alt} fetchPriority="high" />
        <figcaption>{materialVisual.caption} — final manufacturer, finish, and configuration are confirmed in the written quote.</figcaption>
      </figure>

      <section className="budget-live-result__guide" aria-label={`Download the ${material.name} guide with the current budget selection`}>
        <div>
          <span>Selected-budget material guide</span>
          <strong>Take this plan with you.</strong>
          <p>Includes this material, compartment count, selected additions, accessibility direction, and matching material reference pages.</p>
        </div>
        <a href={getPartitionBudgetMaterialGuidePdfUrl(input)} className="button button--primary" download onClick={onDownloadGuide}>
          Download your guide <Download aria-hidden="true" />
        </a>
      </section>

      <div className="budget-live-result__timing">
        <span>Planning lead-time context</span>
        <strong>{PARTITION_BUDGET_TYPICAL_LEAD_TIME}</strong>
        <p>{PARTITION_BUDGET_LEAD_TIME_BOUNDARY}</p>
      </div>

      <button type="button" className="button button--black budget-live-result__cta" onClick={onSendToQuote}>
        Send this budget for a quote <ArrowUpRight aria-hidden="true" />
      </button>
      <p className="budget-live-result__notice"><CircleAlert aria-hidden="true" /> Planning range only—not a quote, order total, availability statement, or timing commitment. Written quote review confirms field verification, manufacturer/series, freight, tax, accessories, timing, and project requirements.</p>
    </aside>
  );
}

export default function PartitionBudgetCalculator() {
  const [, setLocation] = useLocation();
  const [input, setInput] = useState<PartitionBudgetInput>(defaultInput);
  const material = getPartitionBudgetMaterial(input.materialId)!;
  const estimate = useMemo(() => getPartitionBudgetEstimate(input), [input]);
  const materialVisual = partitionBudgetMaterialProductVisuals[input.materialId];

  const selectMaterial = (materialId: PartitionBudgetMaterialId) => setInput((current) => ({ ...current, materialId }));
  const selectAccessibility = (accessibility: PartitionBudgetAccessibility) => setInput((current) => ({ ...current, accessibility }));
  const selectStallCount = (stalls: PartitionBudgetStallCount) => setInput((current) => ({ ...current, stalls }));
  const togglePrivacy = () => setInput((current) => ({ ...current, privacy: current.privacy === "extra-privacy" ? "standard" : "extra-privacy" }));
  const toggleInstallation = () => setInput((current) => ({ ...current, installation: current.installation === "included" ? "not-included" : "included" }));

  const sendToQuote = () => {
    const handoff = buildPartitionBudgetQuoteHandoff(input);
    if (!handoff) return;
    savePartitionBudgetQuoteHandoff(handoff);
    setLocation("/request-a-quote");
  };
  const downloadGuide = () => trackGa4CalculatorGuideDownload();

  const url = `${siteConfig.url}/partition-budget-calculator`;
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: "Commercial Bathroom Partition Budget Calculator",
    description: "Compare owner-reviewed material planning ranges for commercial bathroom partition materials, then add approved extra-privacy or installation planning allowances before requesting a project quote.",
    isPartOf: { "@id": `${siteConfig.url}/#website` },
    about: { "@id": `${siteConfig.url}/bathroom-partitions#collection` },
    mainEntity: { "@id": `${url}#material-matrix` },
    breadcrumb: { "@id": `${url}#breadcrumb` },
  };
  const matrixSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${url}#material-matrix`,
    name: "Commercial bathroom partition base planning ranges by material",
    numberOfItems: partitionBudgetMaterials.length,
    itemListElement: partitionBudgetMaterials.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: { "@type": "Thing", name: `${item.name} commercial bathroom partition planning direction` },
    })),
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${url}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: siteConfig.url },
      { "@type": "ListItem", position: 2, name: "Bathroom Partitions", item: `${siteConfig.url}/bathroom-partitions` },
      { "@type": "ListItem", position: 3, name: "Partition Budget Calculator", item: url },
    ],
  };

  return (
    <>
      <StructuredData data={[pageSchema, matrixSchema, breadcrumbSchema]} />
      <main className="budget-planner-page">
        <header className="budget-planner-hero section-line">
          <div className="budget-planner-hero__copy">
            <p className="breadcrumb"><Link href="/">Home</Link><span>/</span><Link href="/bathroom-partitions">Bathroom partitions</Link><span>/</span> Budget calculator</p>
            <span className="kicker">Commercial restroom planning tool</span>
            <h1>Build the budget before you build the quote.</h1>
            <p>Choose a material direction, enter the approximate compartment count, then add only the early-plan items that belong in the conversation.</p>
            <div className="budget-planner-hero__proof" aria-label="Calculator highlights">
              <span><Check aria-hidden="true" /> Six material directions</span>
              <span><Check aria-hidden="true" /> Fixed additions, shown clearly</span>
              <span><Check aria-hidden="true" /> Selected-budget guide included</span>
            </div>
            <div className="button-row">
              <a href="#partition-budget-calculator" className="button button--primary">Start your planning budget <ArrowDown aria-hidden="true" /></a>
              <Link href="/bathroom-partitions" className="button button--outline">Compare materials <ArrowUpRight aria-hidden="true" /></Link>
            </div>
          </div>
          <aside className="budget-planner-hero__visual" aria-label="How the partition budget calculator works">
            <figure key={input.materialId}>
              <img src={materialVisual.src} width="900" height="900" alt={materialVisual.alt} fetchPriority="high" />
              <figcaption>{materialVisual.caption} · {material.name}</figcaption>
            </figure>
            <div className="budget-planner-hero__steps">
              <span>How it works</span>
              <ol>
                <li><b>01</b><span>Choose one of six material directions.</span></li>
                <li><b>02</b><span>Set 1–6 compartments and only the additions you need.</span></li>
                <li><b>03</b><span>Download the guide or send the plan for quote review.</span></li>
              </ol>
              <p>Planning ranges only. Final scope, pricing, availability, and timing are confirmed in the written quote.</p>
            </div>
          </aside>
        </header>

        <section id="partition-budget-calculator" className="budget-planner-workspace section-line" aria-labelledby="partition-budget-calculator-title">
          <header className="budget-planner-workspace__heading">
            <div>
              <span className="kicker">Start with what you know</span>
              <h2 id="partition-budget-calculator-title">A quick budget, with the right context.</h2>
            </div>
            <p>Material and compartment count drive the planning range. Extra privacy and installation are optional fixed additions. Accessibility is recorded for quote review only and does not change the numbers.</p>
          </header>

          <div className="budget-planner-layout">
            <div className="budget-planner-form" aria-label="Commercial bathroom partition budget selections">
              <div className="budget-planner-current-plan" aria-label="Current planning selections">
                <span>Current plan</span>
                <strong>{material.name}</strong>
                <b>{input.stalls} {input.stalls === 1 ? "compartment" : "compartments"}</b>
                <p>{estimate?.selectedAdditionsLabel ?? "No additions selected"}{input.accessibility === "accessibility" ? " · Accessibility context" : ""}</p>
              </div>

              <fieldset className="budget-planner-step budget-planner-step--materials">
                <legend><span>01</span> Choose the closest material direction</legend>
                <p className="budget-planner-step__helper">Base planning ranges shown below are per compartment before optional additions.</p>
                <div className="budget-material-grid">
                  {materialRanges.map(({ material: option, materialRange }) => <MaterialOption
                    key={option.id}
                    selected={input.materialId === option.id}
                    materialId={option.id}
                    label={option.name}
                    copy={option.summary}
                    lowCents={materialRange.lowCents}
                    highCents={materialRange.highCents}
                    onClick={() => selectMaterial(option.id)}
                  />)}
                </div>
              </fieldset>

              <fieldset className="budget-planner-step">
                <legend><span>02</span> How many toilet compartments?</legend>
                <div className="budget-count-grid">
                  {partitionBudgetStallCounts.map((count) => <CountOption key={count} selected={input.stalls === count} count={count} onClick={() => selectStallCount(count)} />)}
                </div>
                <div className="budget-planner-mobile-range" aria-live="polite">
                  <span>Current planning range</span>
                  <strong>{estimate ? `${formatPartitionBudgetCurrency(estimate.lowCents)}–${formatPartitionBudgetCurrency(estimate.highCents)}` : "Selection needs review"}</strong>
                  <a href="#budget-result">View budget details <ArrowDown aria-hidden="true" /></a>
                </div>
              </fieldset>

              <fieldset className="budget-planner-step">
                <legend><span>03</span> Add only what belongs in the early plan</legend>
                <p className="budget-planner-step__helper">These optional additions are applied to both ends of the range only when selected.</p>
                <div className="budget-addition-grid">
                  <AddOnToggle selected={input.privacy === "extra-privacy"} title="Extra privacy" addition={`+${formatPartitionBudgetCurrency(EXTRA_PRIVACY_ADDITION_PER_STALL_CENTS)} per compartment`} copy="Turn on when extra privacy is part of the early direction." onClick={togglePrivacy} />
                  <AddOnToggle selected={input.installation === "included"} title="Include installation" addition={`+${formatPartitionBudgetCurrency(INSTALLATION_ADDITION_PER_STALL_CENTS)} per compartment`} copy="Turn on when installation belongs in the early planning allowance." onClick={toggleInstallation} />
                </div>
              </fieldset>

              <fieldset className="budget-planner-step">
                <legend><span>04</span> Record accessibility context for quote review</legend>
                <p className="budget-planner-step__helper">This tells the quote team more about the plan. It does not change the planning range.</p>
                <div className="budget-context-grid">
                  <AccessibilityOption selected={input.accessibility === "standard"} label="Standard configuration" copy="No accessibility direction selected for quote review." onClick={() => selectAccessibility("standard")} />
                  <AccessibilityOption selected={input.accessibility === "accessibility"} label="Accessibility configuration" copy="Records the direction for quote review; no numeric adjustment." onClick={() => selectAccessibility("accessibility")} />
                </div>
              </fieldset>
            </div>

              <BudgetResult input={input} material={material} estimate={estimate} onDownloadGuide={downloadGuide} onSendToQuote={sendToQuote} />
          </div>
        </section>

        <section className="budget-material-explorer section-line" aria-labelledby="budget-material-explorer-title">
          <header>
            <span className="kicker">Compare before you choose</span>
            <h2 id="budget-material-explorer-title">Six material directions, one clear starting point.</h2>
            <p>Every base range is per compartment. Extra privacy adds $150 per compartment, and installation adds $350 per compartment when selected in the calculator.</p>
          </header>
          <div className="budget-material-explorer__grid">
            {materialRanges.map(({ material: option, materialRange, visual }) => <article key={option.id}>
              <figure><img src={visual.src} width="900" height="900" alt={visual.alt} loading="lazy" /><figcaption>{visual.caption}</figcaption></figure>
              <div>
                <span>{option.name}</span>
                <strong>{formatPartitionBudgetCurrency(materialRange.lowCents)}–{formatPartitionBudgetCurrency(materialRange.highCents)}</strong>
                <p>Base material planning range / compartment</p>
                <Link href={option.materialPath}>Read material guide <ArrowUpRight aria-hidden="true" /></Link>
              </div>
            </article>)}
          </div>
        </section>

        <section className="budget-planner-boundaries section-line" aria-labelledby="budget-planner-boundaries-title">
          <div>
            <span className="kicker kicker--light">Use the range well</span>
            <h2 id="budget-planner-boundaries-title">Good planning begins with a clear next step.</h2>
          </div>
          <div className="budget-planner-boundaries__checklist">
            <article><Sparkles aria-hidden="true" /><h3>Included in this planning tool</h3><p>Material direction, compartment count, selected extra privacy, selected installation, and accessibility context for quote review.</p></article>
            <article><ClipboardList aria-hidden="true" /><h3>Confirmed in the written quote</h3><p>Field dimensions, manufacturer/series, mounting, screens, accessories, freight, tax, availability, timing, installation scope, and final project pricing.</p></article>
          </div>
          <button type="button" className="button button--light budget-planner-boundaries__cta" onClick={sendToQuote}>Send this budget for quote review <ArrowUpRight aria-hidden="true" /></button>
        </section>
        <p className="budget-planner-updated">Owner-approved base material planning allowances reviewed for Partition Stalls in {PARTITION_BUDGET_UPDATED_LABEL}, including the October 16, 2026 15% lower-end update with original upper ceilings retained and October 25 fixed additions. Not a final price or availability commitment.</p>
      </main>
    </>
  );
}
